// Store
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetSearchInfo } from "store/slice/actions";
import { customAsyncThunk } from "store/slice/slices.functions";

// Store
import { RootState } from "app/store";

// Types
import { RequestInfo } from "store/slice/store.types";
import { AllBriefChildren, BriefChildren, BriefChildTypes } from "services/projects/project.types";
import { fetchProjectByIdService } from "services/projects/project.service";
import { differenceBy } from "lodash";

type TBriefsSlice = {
    status: RequestInfo;
    data: BriefChildren[];
};

const initialState: TBriefsSlice = {
    status: "pristine",
    data: [] as BriefChildren[],
};

export const fetchProjectBrief = customAsyncThunk<BriefChildren, { projectId: number | string; path: string }>(
    "project/briefs/getBrief",
    async ({ projectId, path }, { getState }) => {
        const {
            project: {
                briefs: { data: brief },
            },
        } = getState();

        const briefData = brief.find((brief) => brief.meta.path === path);

        if (briefData) {
            return briefData;
        } else {
            const { data } = await fetchProjectByIdService<BriefChildren>(Number(projectId), path);
            return data;
        }
    }
);

const BriefsSlice = createSlice({
    name: "project/briefs",
    initialState,
    reducers: {
        addBriefsData: (state, action: PayloadAction<BriefChildren[] | undefined>) => {
            const existingBriefs = state.data;
            const newBriefs = action.payload || [];
            const newValues = differenceBy(newBriefs, existingBriefs, "data.id");
            return { ...state, data: [...state.data, ...newValues] };
        },
        updateBrief: (state, action: PayloadAction<BriefChildren>) => {
            const path = action.payload.data.id;
            const brief = state.data.find((brief) => brief.data.id === path);
            if (brief) {
                const briefChildren = state.data.map((briefChild) => {
                    return briefChild.data.id === brief.data.id ? action.payload : briefChild;
                });
                return { ...state, data: briefChildren };
            }
            return state;
        },
        updateBriefChild: (
            state,
            action: PayloadAction<{
                data: AllBriefChildren;
                id: number;
                type: BriefChildTypes;
            }>
        ) => {
            const { data, id, type } = action.payload;
            const brief = state.data.find((brief) => brief.data.id === id);
            if (brief) {
                const briefChildren = brief.children.map((briefChild) => {
                    return briefChild.meta.type === type ? data : briefChild;
                });
                const updatedbrief = { ...brief, children: briefChildren };
                const briefs = state.data.map((brief) => {
                    return brief.data.id === id ? updatedbrief : brief;
                });

                return { ...state, data: briefs };
            }
            return state;
        },
        clearBriefsData: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(fetchProjectBrief.pending, (state) => ({ ...state, status: "fetching" }));
        builder.addCase(fetchProjectBrief.rejected, (state) => ({ ...state, status: "error" }));
        builder.addCase(fetchProjectBrief.fulfilled, (state, action) => ({
            ...state,
            data: state.data.find((d) => d.meta.path === action.payload.meta.path)
                ? state.data
                : [...state.data, action.payload],
            status: "done",
        }));
        builder.addCase(resetSearchInfo, () => initialState);
    },
});

export const { addBriefsData, updateBrief, updateBriefChild, clearBriefsData } = BriefsSlice.actions;

export const getBriefs = (store: RootState) => store.project.briefs;
export const getBriefById = (briefId: number) => (store: RootState) => ({
    data: store.project.briefs.data.find((brief) => brief.data.id === Number(briefId)),
    status: store.project.briefs.status,
});

export default BriefsSlice.reducer;
